<template>
  <div class="mt-2">
    <p v-if="title" class="text-caption text-primary-300 mb-1 text-center">{{ title }}</p>
    <div class="chatbot-map-wrapper rounded-lg overflow-hidden" style="height: 220px">
      <Map :center="center" :zoom="12">
        <template v-slot:markers>
          <l-marker v-for="(point, index) in points" :key="index" :lat-lng="[point.latitude, point.longitude]">
            <l-popup v-if="point.popupText">{{ point.popupText }}</l-popup>
          </l-marker>
        </template>
      </Map>
      <button type="button" class="chatbot-map-expand-btn" :title="t('chatbot.map-expand')" @click="expanded = true">
        <v-icon size="20" color="white">mdi-arrow-expand-all</v-icon>
      </button>
    </div>

    <Teleport to="body">
      <Transition name="map-fullscreen">
        <div v-if="expanded" class="map-fullscreen-overlay" @click.self="expanded = false">
          <div class="map-fullscreen-inner rounded-lg overflow-hidden">
            <p v-if="title" class="map-fullscreen-title">{{ title }}</p>
            <div class="map-fullscreen-map">
              <Map :center="center" :zoom="13">
                <template v-slot:markers>
                  <l-marker
                    v-for="(point, index) in points"
                    :key="`fs-${index}`"
                    :lat-lng="[point.latitude, point.longitude]"
                  >
                    <l-popup v-if="point.popupText">{{ point.popupText }}</l-popup>
                  </l-marker>
                </template>
              </Map>
            </div>
          </div>
          <button type="button" class="map-fullscreen-close" :title="t('chatbot.map-close')" @click="expanded = false">
            <v-icon size="28" color="white">mdi-close</v-icon>
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { LMarker, LPopup } from "@vue-leaflet/vue-leaflet";

import Map from "@/components/molecules/Map.vue";
import { ChatbotMapPoint } from "@/services/chatbot-service/models";

const { t } = useI18n();

const props = defineProps<{
  points: ChatbotMapPoint[];
  title?: string;
}>();

const expanded = ref(false);

const center = computed<number[]>(() => {
  if (props.points.length === 0) {
    return [0, 0];
  }

  const lat = props.points.reduce((sum, p) => sum + p.latitude, 0) / props.points.length;
  const lng = props.points.reduce((sum, p) => sum + p.longitude, 0) / props.points.length;
  return [lat, lng];
});

// Close the fullscreen map with the Escape key while it is open.
function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    expanded.value = false;
  }
}

watch(expanded, (isOpen) => {
  if (isOpen) {
    window.addEventListener("keydown", onKeydown);
  } else {
    window.removeEventListener("keydown", onKeydown);
  }
});

onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
.chatbot-map-wrapper {
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.chatbot-map-expand-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  transition: background 0.15s ease;
}

.chatbot-map-expand-btn:hover {
  background: rgba(0, 0, 0, 0.75);
}

.map-fullscreen-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4vh 4vw;
}

.map-fullscreen-inner {
  display: flex;
  flex-direction: column;
  width: 92vw;
  height: 88vh;
  background: #1d2636;
}

.map-fullscreen-title {
  color: white;
  text-align: center;
  font-size: 0.9rem;
  padding: 8px;
  margin: 0;
}

.map-fullscreen-map {
  flex: 1 1 auto;
  min-height: 0;
}

.map-fullscreen-close {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
}

.map-fullscreen-close:hover {
  background: rgba(0, 0, 0, 0.75);
}

.map-fullscreen-enter-active,
.map-fullscreen-leave-active {
  transition: opacity 0.2s ease;
}

.map-fullscreen-enter-from,
.map-fullscreen-leave-to {
  opacity: 0;
}
</style>
